import React, { useState, useEffect } from 'react'
import PropTypes from 'prop-types'
import { 
  CheckCircle, 
  Copy, 
  Download, 
  FileText, 
  BookOpen, 
  Clock, 
  Loader, 
  AlertCircle,
  CheckCircle2,
  ChevronDown,
  ChevronUp,
  ExternalLink
} from 'lucide-react'
import ReactMarkdown from 'react-markdown'
import remarkGfm from 'remark-gfm'
import { cn, buttonVariants } from '../../utils/designSystem'
import { formatProgress, estimateTimeRemaining, parseMarkdownContent, formatSourceToBibliography, countWords } from './bukuUtils'
import AgentHeader from '../../components/AgentHeader'

// Source Card Component
const SourceCard = ({ source, index }) => {
  // Extract domain from URL for favicon
  const getDomainFromUrl = (url) => {
    try {
      const domain = new URL(url).hostname
      return domain.replace('www.', '')
    } catch {
      return 'unknown'
    }
  }

  const domain = getDomainFromUrl(source.url)
  const faviconUrl = `https://www.google.com/s2/favicons?domain=${domain}&sz=32`

  return (
    <div className="bg-bg-tertiary rounded-lg p-3 sm:p-4 border border-border-secondary hover:border-border-primary transition-colors min-h-[120px] sm:h-32 flex flex-col">
      <div className="flex items-start space-x-2 sm:space-x-3 flex-1">
        <div className="flex-shrink-0">
          <img 
            src={faviconUrl}
            alt={`${domain} favicon`}
            className="w-5 h-5 sm:w-6 sm:h-6 rounded"
            onError={(e) => {
              e.target.style.display = 'none'
              e.target.nextSibling.style.display = 'block'
            }}
          />
          <div className="w-5 h-5 sm:w-6 sm:h-6 bg-emerald-500/10 rounded flex items-center justify-center" style={{display: 'none'}}>
            <BookOpen className="w-3 h-3 sm:w-4 sm:h-4 text-emerald-500" />
          </div>
        </div>
        <div className="flex-1 min-w-0 flex flex-col">
          <div className="text-xs sm:text-sm font-medium text-text-primary mb-1">
            {domain}
          </div>
          <div className="text-sm font-semibold text-text-primary leading-tight flex-1" style={{
            display: '-webkit-box',
            WebkitLineClamp: 2,
            WebkitBoxOrient: 'vertical',
            overflow: 'hidden',
            textOverflow: 'ellipsis'
          }}>
            {source.title}
          </div>
          {source.authors && (
            <div className="text-xs text-text-tertiary mt-1 line-clamp-1">
              {source.authors.join(', ')}
            </div>
          )}
          {(source.year || source.journal) && (
            <div className="flex items-center space-x-2 text-xs text-text-tertiary mt-1">
              {source.year && (
                <span className="bg-emerald-500/10 text-emerald-400 px-1.5 py-0.5 rounded">
                  {source.year}
                </span>
              )}
              {source.journal && (
                <span className="bg-blue-500/10 text-blue-400 px-1.5 py-0.5 rounded truncate max-w-[120px]">
                  {source.journal}
                </span>
              )}
            </div>
          )}
        </div>
      </div>
      <div className="mt-2 pt-2 border-t border-border-secondary">
        <a 
          href={source.url} 
          target="_blank" 
          rel="noopener noreferrer"
          className="inline-flex items-center text-xs text-emerald-400 hover:text-emerald-300 transition-colors"
        >
          <ExternalLink className="w-3 h-3 mr-1" />
          <span className="hidden sm:inline">Buka Sumber</span>
          <span className="sm:hidden">Buka</span>
        </a>
      </div>
    </div>
  )
}

// Reference Card Component
const ReferenceCard = ({ reference, index }) => {
  return (
    <div className="bg-bg-tertiary rounded-lg p-3 sm:p-4 border border-border-secondary hover:border-border-primary transition-colors h-full">
      <div className="flex items-start space-x-2 sm:space-x-3 h-full">
        <div className="p-1.5 sm:p-2 bg-blue-500/10 rounded-lg flex-shrink-0">
          <BookOpen className="w-3 h-3 sm:w-4 sm:h-4 text-blue-500" />
        </div>
        <div className="flex-1 min-w-0 flex flex-col">
          <div className="text-xs sm:text-sm font-medium text-text-primary mb-1">
            Referensi {index + 1}
          </div>
          <div className="text-xs sm:text-sm text-text-secondary leading-relaxed break-words overflow-hidden flex-1">
            {reference}
          </div>
          {reference.includes('http') && (
            <div className="mt-2 pt-2">
              <a 
                href={reference.match(/https?:\/\/[^\s)]+/)?.[0]} 
                target="_blank" 
                rel="noopener noreferrer"
                className="inline-flex items-center text-xs text-blue-400 hover:text-blue-300 transition-colors"
              >
                <ExternalLink className="w-3 h-3 mr-1" />
                <span className="hidden sm:inline">Buka Referensi</span>
                <span className="sm:hidden">Buka</span>
              </a>
            </div>
          )}
        </div>
      </div>
    </div>
  )
}

// Markdown Content Component (without citations and references)
const MarkdownContent = ({ content }) => {
  // Remove citations from content for display
  let cleanContent = content.replace(/\(([^)]+, \d{4}[^)]*)\)/g, '')
  
  // Remove reference sections that might appear at the end of chapters
  // This removes various forms of reference/bibliography sections
  cleanContent = cleanContent
    // Remove "Referensi" or "Daftar Pustaka" sections
    .replace(/\n\s*#{1,6}\s*(Referensi|Daftar Pustaka|References|Bibliography)\s*\n[\s\S]*$/gi, '')
    // Remove numbered reference lists at the end
    .replace(/\n\s*\d+\.\s+[^\n]*\([^)]*\d{4}[^)]*\)[\s\S]*$/g, '')
    // Remove reference lists with author-year format
    .replace(/\n\s*[A-Z][^\n]*\(\d{4}\)[\s\S]*$/g, '')
    // Remove any remaining citation patterns
    .replace(/\[[^\]]*\]/g, '')
    // Clean up multiple newlines
    .replace(/\n\s*\n\s*\n/g, '\n\n')
    .trim()
  
  return (
    <div className="prose prose-invert max-w-none text-text-secondary leading-relaxed markdown-content">
      <ReactMarkdown
        remarkPlugins={[remarkGfm]}
        components={{
          h1: ({ children }) => <h1 className="text-2xl font-bold text-text-primary mb-4 mt-6">{children}</h1>,
          h2: ({ children }) => <h2 className="text-xl font-semibold text-text-primary mb-3 mt-5">{children}</h2>,
          h3: ({ children }) => <h3 className="text-lg font-medium text-text-primary mb-2 mt-4">{children}</h3>,
          h4: ({ children }) => <h4 className="text-base font-medium text-text-primary mb-2 mt-3">{children}</h4>,
          p: ({ children }) => <p className="mb-4 text-text-secondary leading-relaxed">{children}</p>,
          ul: ({ children }) => <ul className="ml-6 mb-4 space-y-1 text-text-secondary" style={{ listStylePosition: 'outside' }}>{children}</ul>,
          ol: ({ children }) => <ol className="ml-6 mb-4 space-y-1 text-text-secondary" style={{ listStylePosition: 'outside' }}>{children}</ol>,
          li: ({ children }) => <li className="text-text-secondary leading-relaxed ml-2">{children}</li>,
          blockquote: ({ children }) => (
            <blockquote className="border-l-4 border-emerald-500 pl-4 py-2 my-4 bg-emerald-500/5 italic text-text-secondary">
              {children}
            </blockquote>
          ),
          code: ({ inline, children }) => {
            if (inline) {
              return <code className="bg-bg-tertiary px-1 py-0.5 rounded text-sm text-emerald-400">{children}</code>
            }
            return (
              <pre className="bg-bg-tertiary p-4 rounded-lg overflow-x-auto mb-4">
                <code className="text-sm text-text-secondary">{children}</code>
              </pre>
            )
          },
          table: ({ children }) => (
            <div className="overflow-x-auto mb-4">
              <table className="min-w-full border border-border-secondary rounded-lg">
                {children}
              </table>
            </div>
          ),
          thead: ({ children }) => <thead className="bg-bg-tertiary">{children}</thead>,
          tbody: ({ children }) => <tbody>{children}</tbody>,
          tr: ({ children }) => <tr className="border-b border-border-secondary">{children}</tr>,
          th: ({ children }) => <th className="px-4 py-2 text-left text-text-primary font-medium">{children}</th>,
          td: ({ children }) => <td className="px-4 py-2 text-text-secondary">{children}</td>,
        }}
      >
        {cleanContent}
      </ReactMarkdown>
    </div>
  )
}

const BukuResultPage = ({ 
  query, 
  outline, 
  generatedContent, 
  academicSources, 
  progress, 
  isLoading, 
  error, 
  isCompleted,
  onBack,
  onBackToSearch,
  bookConfig
}) => {
  const [expandedSections, setExpandedSections] = useState(new Set())
  const [copyStatus, setCopyStatus] = useState(false)

  // Auto-scroll to current section
  useEffect(() => {
    if (isLoading && progress.current < progress.total) {
      const currentSectionElement = document.getElementById(`section-${progress.current}`)
      if (currentSectionElement) {
        currentSectionElement.scrollIntoView({ behavior: 'smooth', block: 'center' })
      }
    }
  }, [progress.current, isLoading])

  // Calculate progress percentage
  const progressPercentage = formatProgress(progress.current, progress.total)
  
  // Calculate time remaining
  const timeRemaining = estimateTimeRemaining(progress.current, progress.total)

  // Calculate word statistics
  const wordStats = React.useMemo(() => {
    if (!isCompleted || Object.keys(generatedContent).length === 0) {
      return { totalWords: 0, sectionStats: [] }
    }

    const sectionStats = outline.map(section => {
      const content = generatedContent[section.id] || ''
      const wordCount = countWords(content)
      return {
        title: section.title,
        current: wordCount,
        target: section.minWords || 0
      }
    })

    const totalWords = sectionStats.reduce((sum, stat) => sum + stat.current, 0)

    return { totalWords, sectionStats }
  }, [generatedContent, outline, isCompleted])

  // Collect all citations
  const allCitations = React.useMemo(() => {
    if (!isCompleted) return []
    
    const citations = new Set()
    Object.values(generatedContent).forEach(content => {
      const contentCitations = parseMarkdownContent(content)
      contentCitations.forEach(citation => citations.add(citation))
    })
    
    // Add academic sources in APA format
    if (academicSources) {
      academicSources.forEach(source => {
        const apaCitation = formatSourceToBibliography(source)
        citations.add(apaCitation)
      })
    }
    
    return Array.from(citations).sort()
  }, [generatedContent, academicSources, isCompleted])

  // Convert markdown to plain text for copying/downloading
  const convertMarkdownToText = (content) => {
    return content
      .replace(/#{1,6}\s+/g, '') // Remove headers
      .replace(/\*\*(.*?)\*\*/g, '$1') // Remove bold
      .replace(/\*(.*?)\*/g, '$1') // Remove italic
      .replace(/\[(.*?)\]\(.*?\)/g, '$1') // Remove links
      .replace(/`(.*?)`/g, '$1') // Remove inline code
      .replace(/```[\s\S]*?```/g, '') // Remove code blocks
      // Remove citations
      .replace(/\(([^)]+, \d{4}[^)]*)\)/g, '')
      // Remove reference sections
      .replace(/\n\s*#{1,6}\s*(Referensi|Daftar Pustaka|References|Bibliography)\s*\n[\s\S]*$/gi, '')
      // Remove numbered reference lists
      .replace(/\n\s*\d+\.\s+[^\n]*\([^)]*\d{4}[^)]*\)[\s\S]*$/g, '')
      // Remove reference lists with author-year format
      .replace(/\n\s*[A-Z][^\n]*\(\d{4}\)[\s\S]*$/g, '')
      // Remove any remaining citation patterns
      .replace(/\[[^\]]*\]/g, '')
      .replace(/\n\s*\n/g, '\n\n') // Normalize line breaks
      .trim()
  }

  const handleCopyAll = async () => {
    try {
      let fullContent = `BUKU: ${query}\n\n`
      
      // Add book metadata
      if (bookConfig) {
        fullContent += `Bahasa: ${bookConfig.language === 'id' ? 'Indonesia' : 'English'}\n`
        fullContent += `Jenis Buku: ${bookConfig.bookType}\n`
        fullContent += `Gaya Penulisan: ${bookConfig.writingStyle}\n\n`
      }
      
      // Add content for each section
      outline.forEach(section => {
        const content = generatedContent[section.id]
        if (content) {
          fullContent += `${section.title}\n${'='.repeat(section.title.length)}\n\n`
          fullContent += convertMarkdownToText(content) + '\n\n'
        }
      })
      
      // Add bibliography
      if (allCitations.length > 0) {
        fullContent += 'DAFTAR PUSTAKA\n===============\n\n'
        allCitations.forEach((citation, index) => {
          fullContent += `${index + 1}. ${citation}\n\n`
        })
      }
      
      await navigator.clipboard.writeText(fullContent)
      setCopyStatus(true)
      setTimeout(() => setCopyStatus(false), 2000)
    } catch (err) {
      console.error('Failed to copy:', err)
    }
  }

  const handleDownload = () => {
    let fullContent = `BUKU: ${query}\n\n`
    
    // Add book metadata
    if (bookConfig) {
      fullContent += `Bahasa: ${bookConfig.language === 'id' ? 'Indonesia' : 'English'}\n`
      fullContent += `Jenis Buku: ${bookConfig.bookType}\n`
      fullContent += `Gaya Penulisan: ${bookConfig.writingStyle}\n\n`
    }
    
    // Add content for each section
    outline.forEach(section => {
      const content = generatedContent[section.id]
      if (content) {
        fullContent += `${section.title}\n${'='.repeat(section.title.length)}\n\n`
        fullContent += convertMarkdownToText(content) + '\n\n'
      }
    })
    
    // Add bibliography
    if (allCitations.length > 0) {
      fullContent += 'DAFTAR PUSTAKA\n===============\n\n'
      allCitations.forEach((citation, index) => {
        fullContent += `${index + 1}. ${citation}\n\n`
      })
    }
    
    const blob = new Blob([fullContent], { type: 'text/plain' })
    const url = URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = `buku-${query.toLowerCase().replace(/\s+/g, '-')}.txt`
    document.body.appendChild(a)
    a.click()
    document.body.removeChild(a)
    URL.revokeObjectURL(url)
  }

  const toggleSection = (sectionId) => {
    const newExpanded = new Set(expandedSections)
    if (newExpanded.has(sectionId)) {
      newExpanded.delete(sectionId)
    } else {
      newExpanded.add(sectionId)
    }
    setExpandedSections(newExpanded)
  }

  return (
    <div className="min-h-screen bg-bg-primary overflow-x-hidden">
      <AgentHeader
        onBack={onBack}
        icon={BookOpen}
        title="Buku Agent"
        query="Buku yang telah dihasilkan berdasarkan outline"
        iconColor="text-purple-500"
        iconBgColor="bg-purple-500/10"
        actions={isCompleted ? [
          {
            icon: copyStatus ? CheckCircle : Copy,
            label: copyStatus ? "Tersalin!" : "Salin Semua",
            onClick: handleCopyAll,
            variant: "secondary",
            className: copyStatus ? "bg-emerald-500/20 text-emerald-400 border-emerald-500/30" : ""
          },
          {
            icon: Download,
            label: "Download",
            onClick: handleDownload,
            variant: "primary"
          }
        ] : []}
      />

      <div className="max-w-6xl mx-auto px-2 sm:px-4 py-3 sm:py-8">
        {/* Topic Display */}
        <div className="bg-bg-secondary rounded-xl p-3 sm:p-6 mb-4 sm:mb-8 border border-border-primary">
          <h2 className="text-base sm:text-lg font-semibold text-text-primary mb-2">Topik Buku</h2>
          <p className="text-text-secondary text-sm sm:text-lg break-words">"{query}"</p>
          
          {/* Book Configuration */}
          {bookConfig && (
            <div className="mt-3 sm:mt-4 grid grid-cols-1 sm:grid-cols-3 gap-2 sm:gap-4">
              <div className="bg-bg-tertiary rounded-lg p-2 sm:p-3">
                <span className="text-xs text-text-tertiary">Bahasa</span>
                <p className="text-sm font-medium text-text-primary">
                  {bookConfig.language === 'id' ? 'Indonesia' : 'English'}
                </p>
              </div>
              <div className="bg-bg-tertiary rounded-lg p-3">
                <span className="text-xs text-text-tertiary">Jenis Buku</span>
                <p className="text-sm font-medium text-text-primary">{bookConfig.bookType}</p>
              </div>
              <div className="bg-bg-tertiary rounded-lg p-3">
                <span className="text-xs text-text-tertiary">Gaya Penulisan</span>
                <p className="text-sm font-medium text-text-primary">{bookConfig.writingStyle}</p>
              </div>
            </div>
          )}
          
          <p className="text-sm text-text-tertiary mt-2">📚 Outline detail untuk struktur buku</p>
        </div>

        {/* Completion Message */}
        {isCompleted && (
          <div className="bg-emerald-500/10 border border-emerald-500/20 rounded-xl p-3 sm:p-6 mb-4 sm:mb-8">
            <div className="flex items-start space-x-2 sm:space-x-3">
              <CheckCircle className="w-5 h-5 sm:w-6 sm:h-6 text-emerald-500 flex-shrink-0 mt-0.5" />
              <div>
                <h3 className="text-sm sm:text-lg font-semibold text-emerald-400">Buku Berhasil Dibuat!</h3>
                <p className="text-xs sm:text-base text-emerald-300 break-words">
                  Semua {progress.total} bagian telah selesai diproses. Anda dapat melihat, menyalin, atau mengunduh hasilnya.
                </p>
              </div>
            </div>
          </div>
        )}

        {/* Word Count Statistics */}
        {isCompleted && Object.keys(generatedContent).length > 0 && (
          <div className="bg-bg-secondary rounded-xl p-3 sm:p-6 mb-4 sm:mb-8 border border-border-primary">
            <h3 className="text-base sm:text-lg font-semibold text-text-primary mb-3 sm:mb-4 flex items-center">
              <FileText className="w-4 h-4 sm:w-5 sm:h-5 mr-2 text-blue-500" />
              Statistik Jumlah Kata
            </h3>
            
            {/* Total Word Count */}
            <div className="bg-bg-tertiary rounded-lg p-4 mb-4">
              <div className="flex items-center justify-between mb-2">
                <span className="text-sm font-medium text-text-primary">Total Kata</span>
                <span className="text-sm font-semibold text-emerald-400">
                  {wordStats.totalWords.toLocaleString()} kata
                </span>
              </div>
              <div className="text-xs text-text-tertiary">
                📖 Jumlah kata keseluruhan dalam buku (~{Math.ceil(wordStats.totalWords / 250)} halaman)
              </div>
            </div>
             
             {/* Section-wise breakdown */}
             {wordStats.sectionStats.length > 0 && (
               <div className="space-y-3">
                 <h4 className="text-sm font-medium text-text-primary mb-3">Detail per Bagian:</h4>
                 {wordStats.sectionStats.map((stat, index) => (
                   <div key={index} className="rounded-lg p-3 bg-bg-tertiary">
                     <div className="flex items-center justify-between mb-2">
                       <span className="text-sm text-text-primary font-medium truncate">
                         {stat.title}
                       </span>
                       <span className="text-xs font-semibold text-emerald-400">
                         {stat.current.toLocaleString()} kata
                       </span>
                     </div>
                   </div>
                 ))}
               </div>
             )}
          </div>
        )}

        {/* Progress Section */}
        {!isCompleted && (
          <div className="bg-bg-secondary rounded-xl p-6 mb-8 border border-border-primary">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-text-primary">Progress Pembuatan Buku</h3>
              <div className="text-right">
                <div className="text-sm font-medium text-text-primary">
                  {progress.current} / {progress.total} bagian
                </div>
                <div className="text-xs text-text-tertiary">
                  {progressPercentage}% selesai
                </div>
              </div>
            </div>
            
            {/* Progress Bar */}
            <div className="w-full bg-bg-tertiary rounded-full h-3 mb-2">
              <div 
                className="bg-emerald-500 h-3 rounded-full transition-all duration-500 ease-out"
                style={{ width: `${progressPercentage}%` }}
              />
            </div>
            
            {/* Warning Text */}
            <p className="text-xs text-text-tertiary mb-4">⚠️ Selama proses sedang berjalan jangan tutup tab browser Anda.</p>
            
            {/* Time Estimate */}
            {timeRemaining && (
              <div className="flex items-center space-x-2 text-sm text-text-tertiary">
                <Clock className="w-4 h-4" />
                <span>Estimasi waktu tersisa: {timeRemaining}</span>
              </div>
            )}
            
            {/* Current Section */}
            {isLoading && progress.current < progress.total && (
              <div className="mt-4 p-4 bg-blue-500/10 border border-blue-500/20 rounded-lg">
                <div className="flex items-center space-x-3">
                  <Loader className="w-5 h-5 text-blue-500 animate-spin" />
                  <div>
                    <p className="font-medium text-blue-400">
                      Sedang membuat konten: {outline[progress.current]?.title}
                    </p>
                  </div>
                </div>
              </div>
            )}
          </div>
        )}

        {/* Academic Sources Section */}
        {academicSources && academicSources.length > 0 && (
          <div className="bg-bg-secondary rounded-xl p-6 mb-8 border border-border-primary">
            <h3 className="text-lg font-semibold text-text-primary mb-4 flex items-center">
              <BookOpen className="w-5 h-5 mr-2 text-emerald-500" />
              Sumber Referensi ({academicSources.length})
            </h3>
            <p className="text-sm text-text-tertiary mb-4">
              Sumber-sumber yang digunakan sebagai referensi dalam pembuatan buku ini.
            </p>
            
            {/* Responsive grid layout */}
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {academicSources.map((source, index) => (
                <SourceCard key={index} source={source} index={index} />
              ))}
            </div>
          </div>
        )}

        {/* Error Display */}
        {error && (
          <div className="bg-red-500/10 border border-red-500/20 rounded-xl p-4 mb-6">
            <div className="flex items-start space-x-3">
              <AlertCircle className="w-5 h-5 text-red-500 mt-0.5 flex-shrink-0" />
              <div>
                <h3 className="font-medium text-red-400 mb-1">Terjadi Kesalahan</h3>
                <p className="text-sm text-red-300">{error}</p>
              </div>
            </div>
          </div>
        )}

        {/* Generated Content */}
        <div className="space-y-6">
          <div className="flex items-center justify-between">
            <h2 className="text-xl font-semibold text-text-primary">Hasil Buku</h2>
            {Object.keys(generatedContent).length > 0 && (
              <div className="text-sm text-text-tertiary">
                {Object.keys(generatedContent).length} / {outline.length} bagian selesai
              </div>
            )}
          </div>

          {outline.map((section, index) => {
            const content = generatedContent[section.id]
            const isExpanded = expandedSections.has(section.id)
            const isGenerated = !!content
            const isCurrent = !isCompleted && index === progress.current
            const isCompleted_section = index < progress.current
            const isDaftarPustaka = section.id === 'daftar_pustaka'

            return (
              <div key={section.id} className={cn(
                "bg-bg-secondary rounded-xl border overflow-hidden transition-all",
                isCurrent ? "border-blue-500/50 shadow-lg shadow-blue-500/10" : "border-border-primary",
                isCompleted_section && "border-emerald-500/30"
              )}>
                <div className="p-6">
                  <div className="flex items-center justify-between mb-4">
                    <div className="flex items-center space-x-3 flex-1">
                      <div className={cn(
                        "w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium flex-shrink-0",
                        isCompleted_section ? "bg-emerald-500/20 text-emerald-400" :
                        isCurrent ? "bg-blue-500/20 text-blue-400" :
                        "bg-bg-tertiary text-text-tertiary"
                      )}>
                        {isCompleted_section ? (
                          <CheckCircle className="w-4 h-4" />
                        ) : isCurrent ? (
                          <Loader className="w-4 h-4 animate-spin" />
                        ) : (
                          index + 1
                        )}
                      </div>
                      <div className="flex-1 min-w-0">
                        <h3 className="text-lg font-semibold text-text-primary">{section.title}</h3>
                      </div>
                    </div>
                    
                    {(isGenerated || (isDaftarPustaka && isCompleted)) && (
                      <div className="flex-shrink-0">
                        <button
                          onClick={() => toggleSection(section.id)}
                          className={cn(buttonVariants.secondary, "text-sm flex items-center")}
                        >
                          {isExpanded ? 'Sembunyikan' : 'Tampilkan'}
                        </button>
                      </div>
                    )}
                  </div>
                  
                  {/* Content */}
                  {isGenerated && isExpanded && !isDaftarPustaka && (
                    <div className="mt-4 p-4 bg-bg-primary rounded-lg border border-border-secondary">
                      <MarkdownContent content={content} />
                    </div>
                  )}
                  
                  {/* Daftar Pustaka Content */}
                  {isDaftarPustaka && isExpanded && isCompleted && (
                    <div className="mt-4 p-4 bg-bg-primary rounded-lg border border-border-secondary">
                      <div className="space-y-6">
                        <div className="prose prose-invert max-w-none text-text-secondary leading-relaxed">
                          <h4 className="text-lg font-semibold text-text-primary mb-4 flex items-center">
                            <BookOpen className="w-5 h-5 mr-2" />
                            Daftar Pustaka ({allCitations.length})
                          </h4>
                          <p className="text-sm text-text-tertiary mb-4">
                            Semua referensi yang digunakan dalam buku ini.
                          </p>
                          {allCitations.length > 0 ? (
                             <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                               {allCitations.map((citation, index) => (
                                 <ReferenceCard key={index} reference={citation} index={index} />
                               ))}
                             </div>
                           ) : (
                             <p className="text-text-tertiary italic">Tidak ada referensi yang ditemukan.</p>
                           )}
                        </div>
                      </div>
                    </div>
                  )}
                  
                  {/* Loading State */}
                  {isCurrent && isLoading && (
                    <div className="mt-4 p-4 bg-blue-500/5 rounded-lg border border-blue-500/20">
                      <div className="flex items-center space-x-3">
                        <Loader className="w-5 h-5 text-blue-500 animate-spin" />
                        <span className="text-blue-400">Sedang menggenerate konten...</span>
                      </div>
                    </div>
                  )}
                  
                  {/* Pending State */}
                  {!isGenerated && !isCurrent && !isDaftarPustaka && (
                    <div className="mt-4 p-4 bg-bg-tertiary rounded-lg">
                      <span className="text-text-tertiary">Menunggu untuk diproses...</span>
                    </div>
                  )}
                  
                  {/* Daftar Pustaka Pending State */}
                  {isDaftarPustaka && !isCompleted && (
                    <div className="mt-4 p-4 bg-bg-tertiary rounded-lg">
                      <span className="text-text-tertiary">Daftar pustaka akan tersedia setelah semua bagian selesai...</span>
                    </div>
                  )}
                </div>
              </div>
            )
          })}
        </div>
      </div>
    </div>
  )
}

BukuResultPage.propTypes = {
  query: PropTypes.string.isRequired,
  outline: PropTypes.array.isRequired,
  generatedContent: PropTypes.object.isRequired,
  academicSources: PropTypes.array,
  progress: PropTypes.shape({
    current: PropTypes.number.isRequired,
    total: PropTypes.number.isRequired
  }).isRequired,
  isLoading: PropTypes.bool.isRequired,
  error: PropTypes.string,
  isCompleted: PropTypes.bool.isRequired,
  onBack: PropTypes.func.isRequired,
  onBackToSearch: PropTypes.func.isRequired,
  bookConfig: PropTypes.shape({
    language: PropTypes.string,
    bookType: PropTypes.string,
    writingStyle: PropTypes.string
  })
}

export default BukuResultPage